import type { LootCache, Item, Pool, Instance } from "./types";

/**
 * Mock loot cache — Midnight Season 1 placeholder.
 *
 * Structure mirrors what the addon's `LootData:Rebuild()` produces from the
 * Encounter Journal: instances + pools + items, with per-item spec eligibility.
 *
 * NOT canonical Midnight S1 content yet. Names are season-flavored placeholders
 * and will be replaced by real data once the addon exports its scraped cache
 * (or the Blizzard API integration lands). Item icon names reference Wowhead's
 * CDN: https://wow.zamimg.com/images/wow/icons/large/{name}.jpg
 *
 * Convention:
 *   poolID "d:{instanceID}"  — dungeon (all bosses merged into one pool)
 *   poolID "b:{encounterID}" — raid boss (one pool per boss)
 */

// All-spec helper: every spec ID across every class, for tokens / universal pieces.
const ALL_SPECS = [
  71, 72, 73,                       // Warrior
  65, 66, 70,                       // Paladin
  253, 254, 255,                    // Hunter
  259, 260, 261,                    // Rogue
  256, 257, 258,                    // Priest
  250, 251, 252,                    // DK
  262, 263, 264,                    // Shaman
  62, 63, 64,                       // Mage
  265, 266, 267,                    // Warlock
  268, 269, 270,                    // Monk
  102, 103, 104, 105,               // Druid
  577, 581,                         // DH
  1467, 1468, 1473,                 // Evoker
];

// Armor type buckets.
const PLATE = [71, 72, 73, 65, 66, 70, 250, 251, 252];                   // War, Pal, DK
const MAIL = [253, 254, 255, 262, 263, 264, 1467, 1468, 1473];           // Hunt, Sham, Evoker
const LEATHER = [259, 260, 261, 268, 269, 270, 102, 103, 104, 105, 577, 581]; // Rog, Monk, Dru, DH
const CLOTH = [256, 257, 258, 62, 63, 64, 265, 266, 267];                // Priest, Mage, Lock

// Stat-ish bucket helpers for weapons.
const STR_DPS = [71, 72, 251, 252];                                      // Arms/Fury War, Frost/Unholy DK
const STR_TANK = [73, 250];                                              // Prot War, Blood DK
const AGI_DPS = [253, 254, 255, 259, 260, 261, 269, 103, 577];           // Hunters, Rogues, WW Monk, Feral, Havoc DH
const AGI_TANK = [104, 581, 268];                                        // Guardian, Vengeance, Brewmaster
const INT_DPS = [62, 63, 64, 265, 266, 267, 102, 258, 1467, 1473, 262];  // Caster DPS
const INT_HEAL = [256, 257, 65, 270, 264, 105, 1468];                    // Healers
const RET_AND_PROT_PALA = [66, 70];

const ITEM_ARRAY: Item[] = [
  // ─── DUNGEON 1: Sunwell Spire ────────────────────────────────────────
  { id: 220101, name: "Sunpriest's Hood",          iconName: "inv_helmet_22", slot: "INVTYPE_HEAD",     quality: "epic", itemLevel: 626, eligibleSpecs: CLOTH,    bossName: "High Priest Vandros" },
  { id: 220102, name: "Felblood Pauldrons",         iconName: "inv_shoulder_36", slot: "INVTYPE_SHOULDER", quality: "epic", itemLevel: 626, eligibleSpecs: PLATE,    bossName: "High Priest Vandros" },
  { id: 220103, name: "Bracers of Dawnstrike",      iconName: "inv_bracer_15", slot: "INVTYPE_WRIST",    quality: "epic", itemLevel: 626, eligibleSpecs: MAIL,     bossName: "Captain Sarothil" },
  { id: 220104, name: "Sun-Bound Ring",             iconName: "inv_jewelry_ring_55", slot: "INVTYPE_FINGER",   quality: "epic", itemLevel: 626, eligibleSpecs: ALL_SPECS, bossName: "Captain Sarothil" },
  { id: 220105, name: "Veilstrider Greatsword",     iconName: "inv_sword_148", slot: "INVTYPE_2HWEAPON", quality: "epic", itemLevel: 626, eligibleSpecs: [...STR_DPS, ...AGI_DPS, 70], bossName: "Magus Lor'themar" },
  { id: 220106, name: "Quel'Sorin Sigil",           iconName: "inv_misc_orb_05", slot: "INVTYPE_TRINKET",  quality: "epic", itemLevel: 626, eligibleSpecs: ALL_SPECS, bossName: "Magus Lor'themar" },

  // ─── DUNGEON 2: Court of Stars Reborn ───────────────────────────────
  { id: 220201, name: "Twilight Veil Mantle",       iconName: "inv_shoulder_24", slot: "INVTYPE_SHOULDER", quality: "epic", itemLevel: 626, eligibleSpecs: CLOTH,    bossName: "Skyspeaker Vela" },
  { id: 220202, name: "Nightborne Spellblade",      iconName: "inv_sword_30", slot: "INVTYPE_WEAPONMAINHAND", quality: "epic", itemLevel: 626, eligibleSpecs: [...INT_DPS, ...INT_HEAL, 256], bossName: "Skyspeaker Vela" },
  { id: 220203, name: "Bracers of Hidden Stars",    iconName: "inv_bracer_27", slot: "INVTYPE_WRIST",    quality: "epic", itemLevel: 626, eligibleSpecs: LEATHER,  bossName: "Talixae the Watchful" },
  { id: 220204, name: "Stonebound Greaves",         iconName: "inv_pants_plate_15", slot: "INVTYPE_LEGS",     quality: "epic", itemLevel: 626, eligibleSpecs: PLATE,    bossName: "Talixae the Watchful" },
  { id: 220205, name: "Locket of the Faithful",     iconName: "inv_jewelry_necklace_15", slot: "INVTYPE_NECK",     quality: "epic", itemLevel: 626, eligibleSpecs: ALL_SPECS, bossName: "Patrol Captain Gerdo" },
  { id: 220206, name: "Sunlit Cinch",               iconName: "inv_belt_42", slot: "INVTYPE_WAIST",    quality: "epic", itemLevel: 626, eligibleSpecs: MAIL,     bossName: "Patrol Captain Gerdo" },

  // ─── DUNGEON 3: Tower of the Sunsworn ───────────────────────────────
  { id: 220301, name: "Helm of the Watchman",       iconName: "inv_helmet_42", slot: "INVTYPE_HEAD",     quality: "epic", itemLevel: 626, eligibleSpecs: PLATE,    bossName: "Magister Sunsworn" },
  { id: 220302, name: "Sunsworn Bow",               iconName: "inv_weapon_bow_06", slot: "INVTYPE_RANGED",   quality: "epic", itemLevel: 626, eligibleSpecs: [253, 254, 255], bossName: "Magister Sunsworn" },
  { id: 220303, name: "Treads of the Faithful",     iconName: "inv_boots_cloth_15", slot: "INVTYPE_FEET",     quality: "epic", itemLevel: 626, eligibleSpecs: CLOTH,    bossName: "Watchful Eye" },
  { id: 220304, name: "Phoenix-Touched Bulwark",    iconName: "inv_shield_06", slot: "INVTYPE_SHIELD",   quality: "epic", itemLevel: 626, eligibleSpecs: [73, 66, 264, 65], bossName: "Watchful Eye" },
  { id: 220305, name: "Sunsworn Signet",            iconName: "inv_jewelry_ring_60", slot: "INVTYPE_FINGER",   quality: "epic", itemLevel: 626, eligibleSpecs: ALL_SPECS, bossName: "Lieutenant Selama" },
  { id: 220306, name: "Tier Token: Conqueror's Sunsworn", iconName: "inv_misc_token_argentdawn3", slot: "INVTYPE_HEAD", quality: "epic", itemLevel: 626, eligibleSpecs: ALL_SPECS, bossName: "Lieutenant Selama", isToken: true },

  // ─── DUNGEON 4: The Twilight Veil ───────────────────────────────────
  { id: 220401, name: "Voidshard Cape",             iconName: "inv_misc_cape_19", slot: "INVTYPE_CLOAK",    quality: "epic", itemLevel: 626, eligibleSpecs: ALL_SPECS, bossName: "Speaker Y'lasiq" },
  { id: 220402, name: "Twilight Veil Robes",        iconName: "inv_chest_cloth_45", slot: "INVTYPE_CHEST",    quality: "epic", itemLevel: 626, eligibleSpecs: CLOTH,    bossName: "Speaker Y'lasiq" },
  { id: 220403, name: "Voidwoven Gloves",           iconName: "inv_gauntlets_25", slot: "INVTYPE_HAND",     quality: "epic", itemLevel: 626, eligibleSpecs: LEATHER,  bossName: "Voidcaller Naazindhri" },
  { id: 220404, name: "Tendril-Wrapped Trinket",    iconName: "inv_misc_orb_03", slot: "INVTYPE_TRINKET",  quality: "epic", itemLevel: 626, eligibleSpecs: ALL_SPECS, bossName: "Voidcaller Naazindhri" },
  { id: 220405, name: "Veil-Touched Belt",          iconName: "inv_belt_36", slot: "INVTYPE_WAIST",    quality: "epic", itemLevel: 626, eligibleSpecs: PLATE,    bossName: "Voidlord K'thalox" },
  { id: 220406, name: "Twilight Hand Cannon",       iconName: "inv_weapon_rifle_06", slot: "INVTYPE_RANGED",   quality: "epic", itemLevel: 626, eligibleSpecs: [253, 254, 255], bossName: "Voidlord K'thalox" },

  // ─── RAID: The Eternal Court (8 bosses) ─────────────────────────────
  // Boss 1: Magister Umbra
  { id: 220801, name: "Crown of Eternal Twilight",  iconName: "inv_helmet_38", slot: "INVTYPE_HEAD",     quality: "epic", itemLevel: 639, eligibleSpecs: CLOTH,    bossName: "Magister Umbra" },
  { id: 220802, name: "Pauldrons of the Eternal",   iconName: "inv_shoulder_15", slot: "INVTYPE_SHOULDER", quality: "epic", itemLevel: 639, eligibleSpecs: MAIL,     bossName: "Magister Umbra" },
  { id: 220803, name: "Twilight Court Gauntlets",   iconName: "inv_gauntlets_15", slot: "INVTYPE_HAND",     quality: "epic", itemLevel: 639, eligibleSpecs: PLATE,    bossName: "Magister Umbra" },

  // Boss 2: Lord Anasterian
  { id: 220804, name: "Anasterian's Bonecage",      iconName: "inv_chest_plate03", slot: "INVTYPE_CHEST",    quality: "epic", itemLevel: 639, eligibleSpecs: PLATE,    bossName: "Lord Anasterian" },
  { id: 220805, name: "Tunic of the Forsworn King", iconName: "inv_chest_leather_06", slot: "INVTYPE_CHEST",    quality: "epic", itemLevel: 639, eligibleSpecs: LEATHER,  bossName: "Lord Anasterian" },
  { id: 220806, name: "Sunwell-Blooded Necklace",   iconName: "inv_jewelry_necklace_71", slot: "INVTYPE_NECK",     quality: "epic", itemLevel: 639, eligibleSpecs: ALL_SPECS, bossName: "Lord Anasterian" },

  // Boss 3: The Twin Sirens
  { id: 220807, name: "Whispering Spaulders",       iconName: "inv_shoulder_27", slot: "INVTYPE_SHOULDER", quality: "epic", itemLevel: 639, eligibleSpecs: CLOTH,    bossName: "The Twin Sirens" },
  { id: 220808, name: "Siren-Song Bracers",         iconName: "inv_bracer_27", slot: "INVTYPE_WRIST",    quality: "epic", itemLevel: 639, eligibleSpecs: LEATHER,  bossName: "The Twin Sirens" },
  { id: 220809, name: "Echo of the Twins",          iconName: "inv_misc_gem_01", slot: "INVTYPE_TRINKET",  quality: "epic", itemLevel: 639, eligibleSpecs: ALL_SPECS, bossName: "The Twin Sirens" },

  // Boss 4: Archon Soldreth
  { id: 220810, name: "Soldreth's Greaves",         iconName: "inv_pants_plate_15", slot: "INVTYPE_LEGS",     quality: "epic", itemLevel: 639, eligibleSpecs: PLATE,    bossName: "Archon Soldreth" },
  { id: 220811, name: "Boots of Eternal Light",     iconName: "inv_boots_cloth_15", slot: "INVTYPE_FEET",     quality: "epic", itemLevel: 639, eligibleSpecs: CLOTH,    bossName: "Archon Soldreth" },
  { id: 220812, name: "Soldreth's Verdict",         iconName: "inv_axe_113", slot: "INVTYPE_2HWEAPON", quality: "epic", itemLevel: 639, eligibleSpecs: [...STR_DPS, ...STR_TANK, 70, 66], bossName: "Archon Soldreth" },

  // Boss 5: The Burning Curator
  { id: 220813, name: "Reliquary Crown",            iconName: "inv_helmet_42", slot: "INVTYPE_HEAD",     quality: "epic", itemLevel: 639, eligibleSpecs: PLATE,    bossName: "The Burning Curator" },
  { id: 220814, name: "Curator's Whispering Veil",  iconName: "inv_misc_cape_25", slot: "INVTYPE_CLOAK",    quality: "epic", itemLevel: 639, eligibleSpecs: ALL_SPECS, bossName: "The Burning Curator" },
  { id: 220815, name: "Tier Token: Vanquisher's Eternal Court", iconName: "inv_misc_token_argentdawn3", slot: "INVTYPE_CHEST", quality: "epic", itemLevel: 639, eligibleSpecs: ALL_SPECS, bossName: "The Burning Curator", isToken: true },

  // Boss 6: Voidqueen Lin'tara
  { id: 220816, name: "Voidqueen's Diadem",         iconName: "inv_helmet_22", slot: "INVTYPE_HEAD",     quality: "epic", itemLevel: 639, eligibleSpecs: CLOTH,    bossName: "Voidqueen Lin'tara" },
  { id: 220817, name: "Tendrils of Lin'tara",       iconName: "inv_jewelry_ring_67", slot: "INVTYPE_FINGER",   quality: "epic", itemLevel: 639, eligibleSpecs: ALL_SPECS, bossName: "Voidqueen Lin'tara" },
  { id: 220818, name: "Eternal Court Cinch",        iconName: "inv_belt_45", slot: "INVTYPE_WAIST",    quality: "epic", itemLevel: 639, eligibleSpecs: LEATHER,  bossName: "Voidqueen Lin'tara" },

  // Boss 7: Knight-Lord Bloodsworn
  { id: 220819, name: "Bloodsworn Chestguard",      iconName: "inv_chest_chain_06", slot: "INVTYPE_CHEST",    quality: "epic", itemLevel: 639, eligibleSpecs: MAIL,     bossName: "Knight-Lord Bloodsworn" },
  { id: 220820, name: "Eternal Standard",           iconName: "inv_axe_113", slot: "INVTYPE_2HWEAPON", quality: "epic", itemLevel: 639, eligibleSpecs: [...STR_DPS, ...AGI_DPS, 70], bossName: "Knight-Lord Bloodsworn" },
  { id: 220821, name: "Sigil of the Court",         iconName: "inv_jewelry_ring_55", slot: "INVTYPE_FINGER",   quality: "epic", itemLevel: 639, eligibleSpecs: ALL_SPECS, bossName: "Knight-Lord Bloodsworn" },

  // Boss 8: The Sunwell Itself
  { id: 220822, name: "Wraps of the Sunwell",       iconName: "inv_misc_cape_19", slot: "INVTYPE_CLOAK",    quality: "legendary", itemLevel: 645, eligibleSpecs: ALL_SPECS, bossName: "The Sunwell Itself" },
  { id: 220823, name: "Heart of the Sunwell",       iconName: "inv_jewelry_amulet_01", slot: "INVTYPE_TRINKET",  quality: "legendary", itemLevel: 645, eligibleSpecs: ALL_SPECS, bossName: "The Sunwell Itself" },
  { id: 220824, name: "Sunwell-Forged Edge",        iconName: "inv_sword_30", slot: "INVTYPE_WEAPONMAINHAND", quality: "legendary", itemLevel: 645, eligibleSpecs: [...STR_DPS, ...AGI_DPS, ...INT_DPS, ...INT_HEAL, 70, 66], bossName: "The Sunwell Itself" },
  { id: 220825, name: "Phoenix-Down Spaulders",     iconName: "inv_shoulder_36", slot: "INVTYPE_SHOULDER", quality: "epic", itemLevel: 639, eligibleSpecs: PLATE,    bossName: "The Sunwell Itself" },
];

// Build registries
const ITEM_REGISTRY: Record<number, Item> = Object.fromEntries(
  ITEM_ARRAY.map((i) => [i.id, i]),
);

// Dungeons
const DUNGEONS_DEF = [
  { instanceID: 5001, name: "Sunwell Spire",           shortName: "SS",  itemRange: [220101, 220106] as [number, number] },
  { instanceID: 5002, name: "Court of Stars Reborn",   shortName: "COS", itemRange: [220201, 220206] as [number, number] },
  { instanceID: 5003, name: "Tower of the Sunsworn",   shortName: "TOS", itemRange: [220301, 220306] as [number, number] },
  { instanceID: 5004, name: "The Twilight Veil",       shortName: "TTV", itemRange: [220401, 220406] as [number, number] },
];

// Raid bosses
const RAID_INSTANCE_ID = 5100;
const RAID_BOSSES_DEF = [
  { encounterID: 6101, name: "Magister Umbra",        items: [220801, 220802, 220803] },
  { encounterID: 6102, name: "Lord Anasterian",       items: [220804, 220805, 220806] },
  { encounterID: 6103, name: "The Twin Sirens",       items: [220807, 220808, 220809] },
  { encounterID: 6104, name: "Archon Soldreth",       items: [220810, 220811, 220812] },
  { encounterID: 6105, name: "The Burning Curator",   items: [220813, 220814, 220815] },
  { encounterID: 6106, name: "Voidqueen Lin'tara",    items: [220816, 220817, 220818] },
  { encounterID: 6107, name: "Knight-Lord Bloodsworn",items: [220819, 220820, 220821] },
  { encounterID: 6108, name: "The Sunwell Itself",    items: [220822, 220823, 220824, 220825] },
];

// Build instances + pools
const INSTANCES: Record<number, Instance> = {};
const POOLS: Record<string, Pool> = {};

for (const d of DUNGEONS_DEF) {
  const poolID = `d:${d.instanceID}`;
  const itemIDs = ITEM_ARRAY
    .filter((i) => i.id >= d.itemRange[0] && i.id <= d.itemRange[1])
    .map((i) => i.id);
  POOLS[poolID] = {
    id: poolID, label: d.name, isRaid: false,
    instanceID: d.instanceID, instanceName: d.name,
    itemOrder: itemIDs,
  };
  INSTANCES[d.instanceID] = {
    id: d.instanceID, name: d.name, shortName: d.shortName,
    isRaid: false, poolIDs: [poolID],
  };
}

const raidPoolIDs: string[] = [];
for (const b of RAID_BOSSES_DEF) {
  const poolID = `b:${b.encounterID}`;
  POOLS[poolID] = {
    id: poolID, label: b.name, isRaid: true,
    instanceID: RAID_INSTANCE_ID, instanceName: "The Eternal Court",
    bossName: b.name, encounterID: b.encounterID,
    itemOrder: b.items,
  };
  raidPoolIDs.push(poolID);
}
INSTANCES[RAID_INSTANCE_ID] = {
  id: RAID_INSTANCE_ID, name: "The Eternal Court", shortName: "EC",
  isRaid: true, poolIDs: raidPoolIDs,
};

export const LOOT_CACHE: LootCache = {
  tier: "Midnight Season 1",
  classID: 11, // built for Druid in mock mode; will be replaced after class pick
  builtAt: "2026-04-25T00:00:00Z",
  instances: INSTANCES,
  pools: POOLS,
  items: ITEM_REGISTRY,
};

/** Convenience: list pools, dungeons first then raid bosses, alpha. */
export const ALL_POOL_IDS = (() => {
  const dungeons = Object.values(POOLS).filter((p) => !p.isRaid).sort((a, b) => a.label.localeCompare(b.label));
  const raids = Object.values(POOLS).filter((p) => p.isRaid);
  return [...dungeons.map((p) => p.id), ...raids.map((p) => p.id)];
})();
